import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { obterIdTenent } from '../../../services/seguranca';
import { useAppSelector } from '../../../store/hooks';
import ModalForm from '../../../components/modal-form';
import { TipoFormulario } from '../../../services/constantes';
import { Campo, ServicoNFSe } from '../../../services/tipos';
import { FaPlus } from 'react-icons/fa';

interface ModalEmitirNotaProps {
    show: boolean;
    onHide: () => void;
    onEmitir: (idServico: string) => Promise<void>;
    carregando?: boolean;
}

const ModalEmitirNota: React.FC<ModalEmitirNotaProps> = ({ show, onHide, onEmitir, carregando }) => {
    const [servicos, setServicos] = useState<any[]>([]);
    const [servicoSelecionado, setServicoSelecionado] = useState<string>('');
    const token = useAppSelector((state) => state.token);

    // Service Creation State
    const [showModalServico, setShowModalServico] = useState(false);
    const [loadingServico, setLoadingServico] = useState(false);

    useEffect(() => {
        if (show) {
            carregarServicos();
        }
    }, [show]);

    const carregarServicos = async () => {
        try {
            const response = await api.get(`/ServicoNFSePorEmpresa/${obterIdTenent(token)}`);
            setServicos(response.data);
            
            // Preserve selection if still valid, otherwise select default
            if (!servicoSelecionado || !response.data.find((s: any) => s.id === servicoSelecionado)) {
                const padrao = response.data.find((s: any) => s.padrao);
                if (padrao) {
                    setServicoSelecionado(padrao.id);
                } else if (response.data.length > 0) {
                    setServicoSelecionado(response.data[0].id);
                }
            }
        } catch (error) {
            console.error("Erro ao carregar serviços NFSe", error);
            toast.error("Erro ao carregar serviços de NFSe.");
        }
    };

    const handleSubmit = async () => {
        if (!servicoSelecionado) {
            toast.warning("Selecione um serviço.");
            return;
        }
        await onEmitir(servicoSelecionado);
    };

    const camposServico: Campo[] = [
        {
            linha: 1,
            coluna: 1,
            largura: 4,
            nome: "codigo",
            label: "Código",
            tipo: "text",
            required: true,
            maximoCaracteres: 20
        },
        {
            linha: 1,
            coluna: 2,
            largura: 4,
            nome: "codigoCnae",
            label: "Código CNAE",
            tipo: "text",
            required: true,
            maximoCaracteres: 20
        },
        {
            linha: 1,
            coluna: 3,
            largura: 4,
            nome: "codigoTributacaoMunicipio",
            label: "Código Trib. Município",
            tipo: "text",
            required: true,
            maximoCaracteres: 20
        },
        {
            linha: 1,
            coluna: 4,
            largura: 4,
            nome: "aliquotaIss",
            label: "Alíquota ISS (%)",
            tipo: "percentual",
            required: false,
        },
        {
            linha: 2,
            coluna: 1,
            largura: 4,
            nome: "codigoNbs",
            label: "Código NBS",
            tipo: "text",
            required: false,
            maximoCaracteres: 20
        },
        {
            linha: 2,
            coluna: 2,
            largura: 4,
            nome: "indOp",
            label: "Ind. Operação",
            tipo: "text",
            required: false,
            maximoCaracteres: 10
        },
        {
            linha: 2,
            coluna: 3,
            largura: 4,
            nome: "cClassTrib",
            label: "C. Class. Trib.",
            tipo: "text",
            required: false,
            maximoCaracteres: 20
        },
        {
            linha: 3,
            coluna: 1,
            largura: 4,
            nome: "responsavelRetencao",
            label: "Resp. Retenção",
            tipo: "select",
            required: false,
            opcoes: [
                { valor: "0", label: "0 - Tomador" },
                { valor: 1, label: "1 - Prestador" },
                { valor: 2, label: "2 - Intermediário" },
                { valor: 3, label: "3 - Nenhum" }
            ]
        },
        {
            linha: 3,
            coluna: 2,
            largura: 4,
            nome: "tipoRetencaoIss",
            label: "Tipo Ret. ISS",
            tipo: "select",
            required: false,
            opcoes: [
                { valor: 0, label: "0 - Retenção" },
                { valor: 1, label: "1 - Normal" },
                { valor: 2, label: "2 - Substituição" },
                { valor: 3, label: "3 - Nenhum" },
                { valor: 4, label: "4 - sRetido Fora Mun." },
                { valor: 5, label: "5 - Devido Fora Não Ret." }
            ]
        },
        {
            linha: 4,
            coluna: 1,
            largura: 12,
            nome: "descricao",
            label: "Descrição do Serviço",
            tipo: "memo",
            required: true,
            tudoMaiusculo: true,
            maximoCaracteres: 255
        },
        {
            linha: 5,
            coluna: 1,
            largura: 12,
            nome: "padrao",
            label: "Serviço Padrão",
            tipo: "checkbox",
            required: false,
        }
    ];

    const handleSalvarServico = async (dados: any) => {
        setLoadingServico(true);
        try {
            dados.aliquotaIss = Number(dados.aliquotaIss ?? 0);
            dados.padrao = Boolean(dados.padrao);
    
            dados.responsavelRetencao = isNaN(Number(dados.responsavelRetencao))
                ? 0
                : Number(dados.responsavelRetencao);
    
            const payload = {
                ...dados,
                idEmpresa: obterIdTenent(token)
            };
    
            const response = await api.post('/ServicoNFSe', payload);
    
            toast.success("Serviço cadastrado com sucesso");
            setShowModalServico(false);
    
            const responseLista = await api.get(
                `/ServicoNFSePorEmpresa/${obterIdTenent(token)}`
            );
            setServicos(responseLista.data);
    
            if (response.data?.id) {
                setServicoSelecionado(response.data.id);
            } else {
                const novo = responseLista.data.find(
                    (s: any) => s.codigo === dados.codigo
                );
                if (novo) setServicoSelecionado(novo.id);
            }
    
        } catch (error: any) {
            toast.error(
                "Erro ao salvar serviço: " +
                (error.response?.data?.Mensagem || "Erro desconhecido")
            );
        } finally {
            setLoadingServico(false);
        }
    };
    

    return (
        <>
            <Modal show={show} onHide={onHide} centered>
                <Modal.Header>
                    <Modal.Title>Emitir Nota Fiscal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="selectServico">
                            <Form.Label>Serviço</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control 
                                    as="select" 
                                    value={servicoSelecionado} 
                                    onChange={(e) => setServicoSelecionado(e.target.value)}
                                >
                                    <option value="">Selecione...</option>
                                    {servicos.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.codigo} | {s.descricao}
                                        </option>
                                    ))}
                                </Form.Control>
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => setShowModalServico(true)}
                                    title="Cadastrar Novo Serviço"
                                >
                                    <FaPlus />
                                </Button>
                            </div>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={carregando}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={carregando || !servicoSelecionado}>
                        {carregando ? 'Emitindo...' : 'Emitir'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ModalForm
                show={showModalServico}
                modo={TipoFormulario.tpCadastro}
                tamanho="lg"
                campos={camposServico}
                carregando={loadingServico}
                onHide={() => setShowModalServico(false)}
                onCancel={() => setShowModalServico(false)}
                onSave={handleSalvarServico}
                titulo="Cadastrar Serviço NFSe"
                apiConfig={{
                    endpoint: "ServicoNFSe",
                    requisicaoBemSucedida: () => {},
                    tratarErro: () => {},
                    idTenentCampo: 'idEmpresa'
                }}
            />
        </>
    );
};

export default ModalEmitirNota;